/**
 * «Open this» asked by the app-wide search for a place that may not be mounted yet: the chronicle event on the
 * table's letopis (the tab is switched first, the panel mounts after), a player's journal entry (the journal panel
 * opens its preview), an action of Подготовка asked at the table («Новый бит» — Studio mounts after the switch).
 *
 * A listener returns true when it served the request (it is then dropped); a tab switcher returns false so the
 * panel that mounts after it still gets it. A request nobody served within a few seconds is dropped — a place that
 * could not mount must not open on its own later.
 */
export type OpenTarget =
  | { readonly kind: 'journal'; readonly id: string }
  | { readonly kind: 'timeline'; readonly id: string }
  | { readonly kind: 'studio-action'; readonly id: 'create-beat' }
  // «Где используется» of the gallery: the table opens the sheet in the HUD / the map as the GM's preview, Подготовка
  // opens them as a tab.
  | { readonly kind: 'sheet'; readonly id: string }
  | { readonly kind: 'page'; readonly id: string };

type Listener = (target: OpenTarget) => boolean;

const PENDING_MS = 3_000;
let pending: { readonly target: OpenTarget; readonly at: number } | null = null;
const listeners = new Set<Listener>();

export function requestOpen(target: OpenTarget): void {
  pending = { target, at: Date.now() };
  for (const fn of [...listeners]) {
    if (fn(target)) {
      pending = null;
      return;
    }
  }
}

/** Subscribes a place; a fresh request made before it mounted is offered at once. */
export function onOpenRequest(fn: Listener): () => void {
  listeners.add(fn);
  const waiting = pending;
  if (waiting !== null) {
    if (Date.now() - waiting.at > PENDING_MS) pending = null;
    else if (fn(waiting.target)) pending = null;
  }
  return (): void => {
    listeners.delete(fn);
  };
}
